import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Menu, X, CircleQuestionMark } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import TabModule from "./TabModule";

import Button from "../Button";

import Spinner from "../../Partials/Loading/Spinner";
import ShowSearchBarResult from "../../Partials/SearchBar";

import { AxisSvg, bankco, profileImage, recentSearch, search } from "../../../assets/svg";

import { useAuth } from "../../../hoc/AuthContext";
import useWindowSize from "../../../hooks/useWindowSize";

import {
  canAccessPublicRewardDetail, canAccessPublicRewardListing, defaultTextColor, getDynamicBackgroundImageUrl,
  getSubdomainInfo, isHomeLandingPage, isMobSDKMode, isPublicPage, isRewardListingLandingPage, LANDING_PAGE, newTheme,
  renderCardInformation, textColor
} from "../../../Utils/CommonFunctions";
import { HOMEPAGE, MODULETABS } from "../../../Utils/Constant";
import { UID } from "../../../Utils/Constants/Login";
import { fetchLocalStorageItem } from "../../../Utils/StorageUtils";

import { CONTACT_US_PAGE, REWARD_LISTING } from "../../../Routes/RoutesContant";

import { HeaderModuleInfo, HeaderWebsiteConfig, useIntegratorTheme } from "../../../hooks/apiHooks/HeaderHook";
import { useAuBankBackground } from "../../../hooks/useAuBankBackground";
import { useIntegratorFonts } from "../../../hooks/useIntegratorFonts";

import Alert from "../Alert";

import "./Header.scss";

import Login from "../../Pages/Login";

import Dropdown from "../../Partials/Dropdown";

import RenderCreditCardDropDown from "../../Partials/Dropdown/RenderCreditCardDropdown";
import ImageWithFallback from "../../Partials/ImagewithFallback";

const OFFLINE_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';

export default function Header() {
  const [showSearchbarMoblie, setShowSearchBarMobile] = useState(false);
  const [showMenuMoblie, setShowMenuMobile] = useState(false);
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  // search bar
  const [searchTerm, SetSearchTerm] = useState("");
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchresult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [recentSearchesState, setRecentSearchesState] = useState(JSON.parse(localStorage.getItem('recentSearches')) || []);
  // Module Tab section
  const [activeTab, setActiveTab] = useState(null);

  const params = useParams();
  // Public URL params must win over cached values so header state follows the active public link.
  const storedSubId = params.subId || localStorage?.getItem('rewardSubId');

  const { isSubdomain, subdomainName } = getSubdomainInfo();
  const subDomain = isSubdomain ? subdomainName : "";
  const subDomainArray = ["axissa", "axisbank", "axis", "unsanctifying-shera-autochthonously", "axisbanksa"];
  const isAxisSubDomain = subDomainArray.includes(subDomain);
  const rewardSubDomain = ["rewards"];
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, setCardInfo, selectedCardData, setSelectedCardData, userData, showLogin, setShowLogin,
  setIsCardChange, setSubVarientIdGlobal, setShowSignUp, showSignUp, creditCard, subVarientIdGlobal } = useAuth();

  const { headerConfigData, isConfigLoading } = HeaderWebsiteConfig();
  const { integratorTheme } = useIntegratorTheme(isSubdomain, newTheme);

  const websiteHeaderInfo = headerConfigData?.Header || {};
  const signupFields = websiteHeaderInfo?.signup_fields || [];
  const isUidSignupEnabled = signupFields.includes(UID);
  const websiteInfo = headerConfigData?.WebsiteConfig || {};
  const homePageType = websiteInfo.landing_page || HOMEPAGE;
  const showSupport = websiteHeaderInfo?.is_contact_us_present || false;
  const publicLevel = integratorTheme?.public_level;
  const normalizedPath = window.location.pathname.replace(/\/+$/, "") || "/";
  const isRewardsRootRoute = normalizedPath === `/${REWARD_LISTING}`;
  const canPublicRewardListing = canAccessPublicRewardListing(publicLevel);
  const canPublicRewardDetail = canAccessPublicRewardDetail(publicLevel);
  const isPublicRewardPreviewRoute = isRewardsRootRoute && canPublicRewardListing;
  const isPublicRewardDetailPreviewRoute = canPublicRewardDetail &&
    /^\/rewards\/(discount|cashback|giftcard|subscription)\/[^/]+\/[^/]+$/.test(normalizedPath);
  const PublicPage = isPublicPage(publicLevel)
    && !(isRewardsRootRoute && isAuthenticated)
    && !(isPublicRewardDetailPreviewRoute && isAuthenticated);
  const isGuestPublicRewardPreview = !isAuthenticated && (isPublicRewardPreviewRoute || isPublicRewardDetailPreviewRoute);
  const shouldUsePathwayPublicHeader = PublicPage && integratorTheme?.pathway && !isGuestPublicRewardPreview;
  const shouldShowPublicTabs = PublicPage && isAuthenticated && !rewardSubDomain.includes(subDomain);
  const shouldShowAuthControls = !PublicPage || isGuestPublicRewardPreview;
  const publicModuleCardId = ((isPublicRewardPreviewRoute || isPublicRewardDetailPreviewRoute) && !isAuthenticated)
    ? (integratorTheme?.default_subvariant || storedSubId)
    : (PublicPage && !isPublicRewardDetailPreviewRoute ? storedSubId : subVarientIdGlobal);
  const storedIntegratorId = fetchLocalStorageItem('rewardIntegratorId');
  const headerPublicIntegratorId = params.integratorId || storedIntegratorId || integratorTheme?.id;
  const headerPublicSubId = params.subId || storedSubId || integratorTheme?.default_subvariant || subVarientIdGlobal;
  const privateRewardDetailRoutePattern = /^\/rewards\/(giftcard|discount|cashback|subscription)\/[^/]+\/[^/]+$/;
  const publicRewardListingRoutePattern = /^\/rewards\/[^/]+\/[^/]+$/;
  const publicRewardDetailRoutePattern = /^\/rewards\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/;
  const isPublicRewardListingUrl = publicRewardListingRoutePattern.test(normalizedPath);
  const isPublicRewardDetailUrl = publicRewardDetailRoutePattern.test(normalizedPath);
  const isPublicRewardUrl = isPublicRewardListingUrl || isPublicRewardDetailUrl;
  const isPrivateRewardListingUrl = isRewardsRootRoute;
  const isPrivateRewardDetailUrl = privateRewardDetailRoutePattern.test(normalizedPath);
  const isPrivateRewardUrl = isPrivateRewardListingUrl || isPrivateRewardDetailUrl;
  const publicRewardListingLink = headerPublicIntegratorId && headerPublicSubId
    ? `/${REWARD_LISTING}/${headerPublicIntegratorId}/${headerPublicSubId}`
    : `/${REWARD_LISTING}`;
  const publicRewardDetailLink = params.brandName && headerPublicIntegratorId && headerPublicSubId && params.id
    ? `/${REWARD_LISTING}/${params.brandName}/${headerPublicIntegratorId}/${headerPublicSubId}/${params.id}`
    : publicRewardListingLink;
  const headerHomeLink = (() => {
    if (isHomeLandingPage(homePageType) || homePageType === HOMEPAGE) {
      return "/";
    }

    if (isRewardListingLandingPage(homePageType)) {
      return isPublicRewardUrl ? publicRewardListingLink : `/${REWARD_LISTING}`;
    }

    if (homePageType === LANDING_PAGE.REWARD_DETAIL_PAGE) {
      if (isPublicRewardDetailUrl) {
        return publicRewardDetailLink;
      }

      if (isPrivateRewardDetailUrl) {
        return normalizedPath;
      }

      return isPublicRewardUrl ? publicRewardListingLink : `/${REWARD_LISTING}`;
    }

    if (isPublicRewardUrl) return publicRewardListingLink;
    if (isPrivateRewardUrl) return `/${REWARD_LISTING}`;

    return "/";
  })();
  const renderHeaderLogoContainer = (children) => (
    <Link to={headerHomeLink} className="linkContainer">
      {children}
    </Link>
  );

  HeaderModuleInfo(PublicPage, publicModuleCardId, isGuestPublicRewardPreview);

  const myAccountPresent = (websiteHeaderInfo?.is_my_account_present && !PublicPage) || false;

  // theme font
  useIntegratorFonts(websiteInfo);
  // AU Bank background color test (temporary, hardcoded — see useAuBankBackground.js)
  useAuBankBackground(subDomain);
  const isMobile = useWindowSize();

  const dynamicPathwayImageUrl = getDynamicBackgroundImageUrl(integratorTheme?.pathway, isMobile);

  const mobileSDKMode = isMobSDKMode(location?.search);
  const currentHostname = window.location.hostname;
  const showOfflineCacheAction = currentHostname === 'localhost' || currentHostname.endsWith('.localhost');
  const queryParams = new URLSearchParams(location.search);
  const authFlowUid = queryParams.get(UID) || fetchLocalStorageItem(UID) || "";
  const signupFlowUid = isUidSignupEnabled ? authFlowUid : "";
  const shouldShowAuthEntry = Boolean(websiteHeaderInfo?.is_signin_present || websiteHeaderInfo?.is_signup_present);
  const authEntryLabel = websiteHeaderInfo?.is_signin_present ? "Sign In" : "Sign Up";
  const shouldShowHeaderSearch = websiteHeaderInfo?.search_type !== "n/a";

  const drawerLoginRef = useRef(null);
  const resultsRef = useRef(null);
  const dropdownRef = useRef(null);
  const signInButtonRef = useRef(null);
  const uidSignupAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert({ show: false, message: '', type: '' });
      }, 5000);
    }
  }, [alert]);

  useEffect(() => {
    // Get the first segment of the current path
    const currentPath = location.pathname.split("/")[1];

    // Find the matching tab based on the path
    let matchedTab = MODULETABS.find((tab) => tab.path === currentPath);

    if (location.pathname.startsWith("/userProfile")) {
      matchedTab = MODULETABS.find((tab) => tab.id === "my_account");
    }

    // Set active tab
    setActiveTab(matchedTab ? matchedTab.id : null);
  }, [location.pathname]);

  const handleAuthClickOutside = useCallback((event) => {
    const isOutsideModal = drawerLoginRef.current && !drawerLoginRef.current.contains(event.target);
    const isOutsideButton = signInButtonRef.current ? !signInButtonRef.current.contains(event.target) : true;
    if (isOutsideModal && isOutsideButton) {
      setShowLogin(false);
      setShowSignUp(false);
      setCardInfo(null);
      setShowSearchBar(false);
      SetSearchTerm('');
    }
  }, [setShowLogin, setShowSignUp]);

  useEffect(() => {
    if (showLogin || showSignUp) {
      document.addEventListener("mousedown", handleAuthClickOutside);
    } else {
      document.removeEventListener("mousedown", handleAuthClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleAuthClickOutside);
    };
  }, [showLogin, showSignUp]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideDropDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpenDrawer) {
      setShowDropdown(false);
    }
  }, [isOpenDrawer]);

  useEffect(() => {
    if (
      uidSignupAutoOpenedRef.current ||
      !signupFlowUid ||
      !shouldShowAuthEntry ||
      isAuthenticated ||
      mobileSDKMode ||
      location.pathname.includes('/auth')
    ) {
      return;
    }

    setShowLogin(false);
    setShowSignUp(true);
    uidSignupAutoOpenedRef.current = true;
  }, [signupFlowUid, shouldShowAuthEntry, isAuthenticated, location.pathname, mobileSDKMode, setShowLogin, setShowSignUp]);

  const handleShowLogin = (e) => {
    if (mobileSDKMode) return;
    if(e) e.stopPropagation();
    setShowLogin(prev => !prev);
    setShowSignUp(false);
    setShowSearchBarMobile(false);
    setShowMenuMobile(false);
    setShowSearchBar(false);
    SetSearchTerm('');
    setSearchResult(null);
  };

  const handleShowSignUp = (e) => {
    if (mobileSDKMode) return;
    if (e) e.stopPropagation();
    setShowSearchBarMobile(false);
    setShowLogin(false);
    setShowSignUp(prev => !prev);
    setShowMenuMobile(false);
    setShowSearchBar(false);
    SetSearchTerm('');
    setSearchResult(null);
  };

  const handleOpenAuthDrawer = (e) => {
    if (!shouldShowAuthEntry) return;

    if (websiteHeaderInfo?.is_signup_present && (!websiteHeaderInfo?.is_signin_present || signupFlowUid)) {
      handleShowSignUp(e);
      return;
    }

    if (websiteHeaderInfo?.is_signin_present) {
      handleShowLogin(e);
    }
  };

  const handleLogout = () => {
    logout(websiteHeaderInfo?.auth_page_type === "full_page");
    setIsOpenDrawer(false);
    setShowMenuMobile(false);
    setShowSearchBar(false);
    setShowLogin(false);
    setShowSignUp(false);
    SetSearchTerm('');
  };

  const handleClearOfflineCache = () => {
    localStorage.removeItem(OFFLINE_CACHE_KEY);
    setShowDropdown(false);
    setAlert({
      show: true,
      message: 'Offline cache removed from local storage.',
      type: 'success',
    });
  };

  // selected card change function
  const handleCardClick = useCallback((event) => {
    event?.stopPropagation?.();
    setShowDropdown((prev) => !prev);
  }, []);

  const closeHeaderDropdowns = useCallback(() => {
    setShowDropdown(false);
  }, []);

  const handleCardSelect = useCallback((card, event) => {
    event?.stopPropagation?.();
    const selectedSubVariantId = card?.sub_variant?.id;

    if (!selectedSubVariantId) return;

    setSelectedCardData(card);
    setSubVarientIdGlobal(selectedSubVariantId);
    setIsCardChange((prev) => !prev);
    localStorage.setItem("selectedCard", JSON.stringify(card));
    localStorage.setItem("subId", JSON.stringify(selectedSubVariantId));
    setShowDropdown(false);
    setIsOpenDrawer(false);
    setShowMenuMobile(false);
    queryClient.invalidateQueries({ queryKey: ['loyalty-card', isAuthenticated, selectedSubVariantId] });
    queryClient.invalidateQueries({ queryKey: ['sub-module'] });
    queryClient.invalidateQueries({ queryKey: ['website-landing-config'] });
    queryClient.invalidateQueries({ queryKey: ['rewards'] });
    if (isPrivateRewardListingUrl) {
      const rewardParams = new URLSearchParams(location.search);
      rewardParams.set("card_id", selectedSubVariantId);
      const nextRewardListingUrl = `/${REWARD_LISTING}?${rewardParams.toString()}`;
      navigate(nextRewardListingUrl, { replace: true });
    }
  }, [
    isAuthenticated, isPrivateRewardListingUrl, location.search, navigate, queryClient, setSelectedCardData, setIsCardChange, 
    setSubVarientIdGlobal,
  ]);

  const handleClickOutsideDropDown = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  const cardDataToShow = selectedCardData;
  const integratorLogoSrc = integratorTheme?.logo || integratorTheme?.bank?.logo || integratorTheme?.brand?.logo || bankco;
  const integratorLogoAlt = integratorTheme?.bank?.name || integratorTheme?.brand?.name || "bankco";
  const normalizedHeaderLogoType = String( websiteInfo?.header_logo_type || "" ).trim().toLowerCase();
  const brandLogoSrc = integratorTheme?.brand?.logo;
  const brandLogoAlt = integratorTheme?.brand?.name || integratorLogoAlt;
  const headerLogoData = (() => {
    if (!isAuthenticated) {
      return {
        src: integratorLogoSrc,
        alt: integratorLogoAlt,
      };
    }

    if (normalizedHeaderLogoType === "subvariant" || normalizedHeaderLogoType === "subvarient") {
      return {
        src: cardDataToShow?.sub_variant?.header_logo || integratorLogoSrc,
        alt: cardDataToShow?.sub_variant?.name || integratorLogoAlt,
      };
    }

    if (normalizedHeaderLogoType === "brand") {
      return {
        src: brandLogoSrc || integratorLogoSrc,
        alt: brandLogoAlt,
      };
    }

    return {
      src: integratorLogoSrc,
      alt: integratorLogoAlt,
    };
  })();

  const handleNavigation = (path) => {
    setIsOpenDrawer(false);
    navigate(path);
    if (isMobile) setShowMenuMobile(false);
  };

  const handleModuleClick = (tab) => {
    setActiveTab(tab.id);
    setIsOpenDrawer(false);
    if (isMobile) setShowMenuMobile(false);
  };

  const renderDropDownHeader = useCallback(() => {
    return (
      <div className="profileDropdown">
        <span className="cursor_pointer">
          <ImageWithFallback src={profileImage} alt={userData?.name} />
        </span>
      </div>
    );
  }, [userData]);

  const renderDropDownBody = useCallback(() => {
    return (
      <>
        <div className="dropDownBody">
          <ImageWithFallback src={profileImage} alt={userData?.name} />
          <div className="imageContainer">
            Hello {userData?.name},
            <div className="font_600">
              {userData?.mobile_number}
            </div>
          </div>
        </div>

        <div className="dropDownBodyContainer">
          <div className="showTabModule">
            <TabModule
              handleModuleClick={handleModuleClick}
              activeTab={activeTab}
              myAccountPresent={myAccountPresent}
              websiteHeaderInfo={websiteHeaderInfo}
              homePageType={homePageType}
            />
          </div>
          <div className="bodyContainer" onClick={handleCardClick} ref={dropdownRef}>
            <ImageWithFallback src={cardDataToShow?.sub_variant?.logo || cardDataToShow?.sub_variant?.payment_network?.logo} alt={cardDataToShow?.sub_variant?.name}
              className="image" loading="lazy" />

            <div className="cardHeader">{renderCardInformation(cardDataToShow, false)}</div>
            {showDropdown ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {showDropdown && (
              <div
                className="showDropdown"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                {creditCard.map((card) => (
                  <div key={card.id} className="cardItem" onClick={(event) => handleCardSelect(card, event)}>
                    {renderCardInformation(card, false)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {!myAccountPresent && (
            <div className="cursor_pointer" onClick={() => handleNavigation('/userProfile')}>My Account</div>
          )}

          {showOfflineCacheAction && (
            <button type="button" className="offlineCacheButton" onClick={handleClearOfflineCache}>
              Clear Offline Cache
            </button>
          )}

          {!mobileSDKMode && <div onClick={() => handleLogout()} className="logout">Logout</div>}
        </div>
      </>
    );
  }, [userData, integratorTheme, handleNavigation, handleLogout, handleClearOfflineCache, myAccountPresent, showOfflineCacheAction, cardDataToShow, showDropdown, handleCardSelect, creditCard, dropdownRef, handleCardClick, mobileSDKMode]);

  function renderAunthenticatedWebView() {
    return (
      <div className={`authenticatedWebViewOption ${isAuthenticated ? "showAuthenticated" : "hideAuthenticated"}`}>
        {isAuthenticated ? (
          <Dropdown
            dropDownHeader={renderDropDownHeader}
            dropdownBody={renderDropDownBody}
            isOpenDrawer={isOpenDrawer}
            setIsOpenDrawer={setIsOpenDrawer}
            isShowIcon={false}
            isHeader={true}
            onClose={closeHeaderDropdowns}
          />
        ) : (
          <>
            {/* {websiteHeaderInfo?.is_signin_present && (<Button theme={integratorTheme} variant="secondary" size="sm"
            className="buttonText" onClick={handleShowLogin} >
              Sign In
            </Button>)} */}

            {shouldShowAuthEntry && !mobileSDKMode && <div ref={signInButtonRef} className="signInButtonInline">
              <Button
                variant="primary"
                size="sm"
                theme={integratorTheme}
                className="buttonText"
                onClick={handleOpenAuthDrawer}
              >
                {authEntryLabel}
              </Button>
            </div>}
          </>
        )}
      </div>
    );
  }

  function renderMobileViewHeaderOption() {
    return (
      !location.pathname.includes('/auth') && (
        <div className={`mobileViewHeaderOption ${isAxisSubDomain ? "axissaMobile" : ""} `}>
          {shouldShowHeaderSearch && (
            <div className="headerMenuSection">
              {showSearchbarMoblie
                ? (<X color="#3B3E63" size={26}
                  onClick={() => {
                    setShowMenuMobile(false);
                    setShowSearchBarMobile(false);
                    SetSearchTerm('');
                    setShowSearchBar(false);
                    setSearchResult(null);
                  }}
                />
                ) : (
                  websiteHeaderInfo?.search_type === "icon_only" ? (
                    <ImageWithFallback src={search} alt="search" loading="lazy"
                      onClick={() => {
                        setShowMenuMobile(false);
                        setShowSearchBarMobile(true);
                        SetSearchTerm(searchTerm);
                        setShowSearchBar(true)
                      }}
                    />
                  ) : (
                    <div className=""></div>
                  ))
              }
            </div>
          )}

          {!isAuthenticated && shouldShowAuthEntry && !mobileSDKMode ? (
            <div ref={signInButtonRef} className="mobileAuthEntry">
              <Button
                variant="primary"
                size="sm"
                theme={integratorTheme}
                className="mobileAuthButton"
                onClick={handleOpenAuthDrawer}
              >
                {authEntryLabel}
              </Button>
            </div>
          ) : isAuthenticated ? (
            <div
              className="menuOption"
              onClick={() => {
                setShowMenuMobile(!showMenuMoblie);
                setShowSearchBarMobile(false);
                setShowSearchBar(false);
                SetSearchTerm('');
                setSearchResult(null);
                setShowLogin(false);
                setShowSignUp(false);
              }}
            >
              {showMenuMoblie
                ? <X color={textColor(integratorTheme)} size={26} />
                : <Menu color={textColor(integratorTheme)} size={26} />
              }
            </div>
          ) : null}
        </div>
      )
    );
  };

  function renderMobileSearchBarView() {
    return (
      <>
        {showSearchbarMoblie && showSearchBar && (
          <div className={`mobileSearchBarSection
          ${showSearchbarMoblie && isMobile ? "flexBox" : "hiddenBox"}`}>
            <ShowSearchBarResult
              setSearchResult={setSearchResult}
              searchTerm={searchTerm}
              SetSearchTerm={SetSearchTerm}
              setSearchLoading={setSearchLoading}
              setShowSearchBar={setShowSearchBar}
              showSearchBar={showSearchBar}
              searchresult={searchresult}
              setRecentSearchesState={setRecentSearchesState}
              recentSearchesState={recentSearchesState}
              resultsRef={resultsRef}
              recentSearch={recentSearch}
              searchLoading={searchLoading}
              setShowMenuMobile={setShowMenuMobile}
              setShowSearchBarMobile={setShowSearchBarMobile}
              websiteHeaderInfo={websiteHeaderInfo}
            />
          </div>
        )}
      </>
    );
  };

  function renderMobileMenubarView() {
    return (
      showMenuMoblie && (
        <div className={`mobileMenuSection ${showMenuMoblie && isMobile ? "showMobile" : "hiddenMobile"}`}>
          {isAuthenticated ? renderDropDownBody() : (
            <div className="menuSection">
              {shouldShowAuthControls && shouldShowAuthEntry &&
                <div className="menuItem">
                  {/* {websiteHeaderInfo?.is_signin_present && (
                    <Button theme={integratorTheme} variant="secondary" size="lg" className="menuText"
                    onClick={handleShowLogin} >
                      Sign In
                    </Button>
                  )} */}
                  {!mobileSDKMode && <div ref={signInButtonRef} className="signInButtonInlineFull">
                    <Button
                      theme={integratorTheme}
                      variant="primary"
                      size="lg"
                      className="menuText"
                      onClick={handleOpenAuthDrawer}
                    >
                      {authEntryLabel}
                    </Button>
                  </div>}
                </div>
              }
            </div>
          )}
        </div>
      )
    );
  }

  // render Login component
  function renderLoginComponent() {
    const handleLoginClick = (show) => {
      setShowLogin(show);
      setShowSignUp(show);
    };

    return (
      shouldShowAuthEntry && !mobileSDKMode && (showLogin || showSignUp) && (
        <div className="loginComponentWrapper">
          <div className="authDrawerContainer" ref={drawerLoginRef}>
            <Login
              setShowLoginForm={(show) => handleLoginClick(show)}
              initialMode={showSignUp ? "signup" : "login"}
              showSignUp={showSignUp}
              showLogin={showLogin}
              showLoginToggle={websiteHeaderInfo?.is_signin_present}
              showSignUpToggle={websiteHeaderInfo?.is_signup_present}
              UId={signupFlowUid}
            />
          </div>
        </div>
      )
    );
  };

  return ( isConfigLoading
  ? <Spinner />
  : (
      <div
        className={`headerWrapper ${isAxisSubDomain ? "axisSubdomain" : ""}`}
        style={{
          "--theme-text-color": textColor(integratorTheme),
          "--theme-default-text-color": defaultTextColor(integratorTheme),
        }}
      >
        <Alert
          message={alert.message}
          show={alert.show}
          setShowAlert={setAlert}
          type={alert.type}
        />
        <div className="headerContainer">
          {isAxisSubDomain ? (
              <div className="axisSvg">
                {renderHeaderLogoContainer(
                  <AxisSvg color={defaultTextColor(integratorTheme)} />
                )}
                <div className="axisPathway"></div>
              </div>
            ) : (
              integratorTheme?.pathway && <ImageWithFallback
                src={dynamicPathwayImageUrl || integratorTheme?.pathway}
                alt="header background"
                className="headerBackgroundImage"
                loading="lazy"
              />
            )
          }

          <div className="headerContentOverlay">
            {shouldUsePathwayPublicHeader ? (
              <>
                {shouldShowHeaderSearch && websiteHeaderInfo?.search_type === "icon_only" && (
                  <div className="pathwaySection">
                    <div className="pathwaySearch">
                      <div className="pathwaySearchBlock">
                        <ImageWithFallback src={search} alt="search" loading="lazy"
                          className="image" onClick={() => setShowSearchBar(true)} />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={`headerBoxContainer ${isAxisSubDomain ? "axisSubdomain" : ""}`}>
                {isAxisSubDomain ? "" : renderHeaderLogoContainer(
                  <ImageWithFallback src={headerLogoData.src} alt={headerLogoData.alt}
                    className="image"
                    loading="lazy"
                  />
                )}

                {!location.pathname.includes('/auth') && (
                  <div className="headerTabSection">
                    {shouldShowPublicTabs && (
                      <div className="publicPage">
                        <TabModule
                          handleModuleClick={handleModuleClick}
                          activeTab={activeTab}
                          websiteHeaderInfo={websiteHeaderInfo}
                          myAccountPresent={myAccountPresent}
                          homePageType={homePageType}
                          hiddenTabIds={!isAuthenticated ? ["reward"] : []}
                        />
                      </div>
                    )}

                    {shouldShowHeaderSearch && websiteHeaderInfo?.search_type === "icon_only" ? (
                      <div className="imageBlock">
                        <ImageWithFallback src={search} alt="search" loading="lazy" className="image" onClick={() => setShowSearchBar(true)} />
                      </div>
                    ) : (<div className=""></div>)}

                    {isAuthenticated ? (
                      <>
                        {!PublicPage && (
                          <>
                            <div className="privateBlock">
                              <TabModule
                                handleModuleClick={handleModuleClick}
                                activeTab={activeTab}
                                myAccountPresent={myAccountPresent}
                                websiteHeaderInfo={websiteHeaderInfo}
                                homePageType={homePageType}
                              />
                            </div>
                            {(websiteInfo?.card_list_type === "BANNER_SIDE_WITH_HEADER" || websiteInfo?.card_list_type === "HEADER") && (
                              <RenderCreditCardDropDown
                                handleCardSelect={handleCardSelect}
                                showDropdown={showDropdown}
                                  cardDataToShow={cardDataToShow}
                                  cards={creditCard}
                                  dropdownRef={dropdownRef}
                                  handleCardClick={handleCardClick}
                              />
                            )}
                          </>
                        )}
                      </>
                    ) : (""
                    )}

                    {showSupport && (<div className={`publicSupport  ${subDomain === "axissa" ? "axissaMobile" : ""}`}
                        onClick={() => navigate(CONTACT_US_PAGE)}>
                      <CircleQuestionMark size={isMobile ? 24 : 16} color={defaultTextColor(integratorTheme)} />

                      <div className="supportText">Support</div>
                    </div>)}

                    {shouldShowAuthControls && renderAunthenticatedWebView()}

                    {shouldShowAuthControls && renderMobileViewHeaderOption()}

                </div>)}
              </div>
            )}
          </div>

          {shouldShowHeaderSearch && renderMobileSearchBarView()}

          {renderMobileMenubarView()}

          {shouldShowHeaderSearch && <ShowSearchBarResult
            setSearchResult={setSearchResult}
            searchTerm={searchTerm}
            SetSearchTerm={SetSearchTerm}
            setRecentSearchesState={setRecentSearchesState}
            recentSearchesState={recentSearchesState}
            resultsRef={resultsRef}
            recentSearch={recentSearch}
            searchLoading={searchLoading}
            setShowMenuMobile={setShowMenuMobile}
            setShowSearchBarMobile={setShowSearchBarMobile}
            setSearchLoading={setSearchLoading}
            setShowSearchBar={setShowSearchBar}
            showSearchBar={showSearchBar}
            searchresult={searchresult}
            PublicPage={PublicPage}
            websiteHeaderInfo={websiteHeaderInfo}
          />}

          {renderLoginComponent()}
        </div>
      </div>
    )
  );
};
